import React from 'react';
import {fromEvent, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {join, isTouchDevice} from '@esgi/ui-kit/utils';
import {TemplateProps} from '../template';
import styles from './practice.module.less';

class PracticeState {
	played: boolean = false;
	btnType: BtnAudioSrc = BtnAudioSrc.None;
}

enum BtnAudioSrc {
	None = 'None',
	Audio = 'https://esgiwebfiles.s3.amazonaws.com/audio/selfassess/a6f71764-6d6e-41b9-b3d3-0f24f1b4b304',
	Back = 'https://esgiwebfiles.s3.amazonaws.com/audio/selfassess/2a53f5a1-7b6a-4dd7-81a5-7a1a240ae8be',
	Next = 'https://esgiwebfiles.s3.amazonaws.com/audio/selfassess/9d091a55-766e-4cdf-be78-87dad694f235',
}

class Practice extends React.Component<TemplateProps> {
	state = new PracticeState();
	private audio = new Audio();
	private destroy$: Subject<void> = new Subject();

	componentDidMount(): void {
		fromEvent(this.audio, 'ended')
			.pipe(takeUntil(this.destroy$))
			.subscribe(_ => this.setState({played: false, btnType: BtnAudioSrc.None}));
	}

	componentWillUnmount() {		
		this.destroy$.next();
	}

	private play(src: BtnAudioSrc) {
		if (this.state.played) {
			return;
		}

		this.audio.src = src;
		this.setState({played: true, btnType: src}, () => this.audio?.play());
	}

	render() { 
		return <div className={styles.questionWrapper}>
			<button
				onTouchStart={() => isTouchDevice() && this.play(BtnAudioSrc.Audio)}
				className={join(styles.exampleBtn, styles.audio, this.state.btnType === BtnAudioSrc.Audio && styles.active)}
				disabled={this.state.played}
				type='button'
				onClick={() => this.play(BtnAudioSrc.Audio)}
			>
				<svg width='54' height='54' viewBox='0 0 54 54' fill='none' xmlns='http://www.w3.org/2000/svg'>
					<circle cx='27' cy='27' r='27' fill='#0088CC'/>
					<circle cx='27' cy='27' r='25.5' stroke='white' strokeOpacity='0.32' strokeWidth='3'/>
					<path d='M12 21.5783C10.8954 21.5783 10 22.4738 10 23.5783V30.5783C10 31.6829 10.8954 32.5783 12 32.5783H16.5049C17.0353 32.5783 17.544 32.789 17.9191 33.1641L23.0858 38.3308C24.3457 39.5907 26.5 38.6984 26.5 36.9166V17.2401C26.5 15.4583 24.3457 14.566 23.0858 15.8259L17.9191 20.9925C17.544 21.3676 17.0353 21.5783 16.5049 21.5783H12ZM34.75 27.0783C34.75 24.9965 33.9804 23.1034 32.7122 21.6554C31.7326 20.537 30.1667 21.4393 30.1667 22.9261V31.2318C30.1667 32.7138 31.7246 33.617 32.7045 32.5051C33.9773 31.0609 34.75 29.1643 34.75 27.0783ZM32.4298 11.6884C31.2981 11.2528 30.1667 12.1544 30.1667 13.3671V13.3671C30.1667 14.2033 30.7227 14.9271 31.4929 15.2528C36.0983 17.2001 39.3333 21.7636 39.3333 27.0783C39.3333 32.3931 36.0983 36.9565 31.4929 38.9039C30.7227 39.2296 30.1667 39.9533 30.1667 40.7896V40.7896C30.1667 42.0022 31.2981 42.9039 32.4298 42.4683C38.6154 40.0872 43 34.096 43 27.0783C43 20.0606 38.6154 14.0694 32.4298 11.6884Z' fill='white'/>
				</svg>
			</button>
			<button
				onTouchStart={() => isTouchDevice() && this.play(BtnAudioSrc.Back)}
				className={join(styles.exampleBtn, styles.back, this.state.btnType === BtnAudioSrc.Back && styles.active)}
				disabled={this.state.played}
				type='button'
				onClick={() => this.play(BtnAudioSrc.Back)}
			>
				<svg width='75' height='75' viewBox='0 0 75 75' fill='none' xmlns='http://www.w3.org/2000/svg'>
					<circle r='36' transform='matrix(-1 0 0 1 37.5 37.5)' stroke='#828282' strokeWidth='3'/>
					<path fillRule='evenodd' clipRule='evenodd' d='M15.8225 35.74C15.0147 36.5253 15.0147 37.8226 15.8225 38.608L34.4316 56.7002C35.6994 57.9328 37.8257 57.0345 37.8257 55.2662V45.6522H59.3042C60.4088 45.6522 61.3042 44.7568 61.3042 43.6522V30.6957C61.3042 29.5911 60.4088 28.6957 59.3042 28.6957H37.8257V19.0818C37.8257 17.3135 35.6994 16.4151 34.4316 17.6478L15.8225 35.74Z' fill='#848484'/>
				</svg>
			</button>
			<button
				onTouchStart={() => isTouchDevice() && this.play(BtnAudioSrc.Next)}
				className={join(styles.exampleBtn, this.state.btnType === BtnAudioSrc.Next && styles.active)}
				disabled={this.state.played}
				type='button'
				onClick={() => this.play(BtnAudioSrc.Next)}
			>
				<svg width='75' height='75' viewBox='0 0 75 75' fill='none' xmlns='http://www.w3.org/2000/svg'>
					<circle cx='37.5' cy='37.5' r='36' fill='#2FB454' stroke='white' strokeWidth='3'/>
					<path fillRule='evenodd' clipRule='evenodd' d='M59.177 35.74C59.9848 36.5253 59.9848 37.8226 59.177 38.608L40.5679 56.7002C39.3001 57.9328 37.1738 57.0345 37.1738 55.2662V47.6522C37.1738 46.5476 36.2783 45.6522 35.1738 45.6522H15.6956C14.591 45.6522 13.6956 44.7568 13.6956 43.6522V30.6957C13.6956 29.5911 14.591 28.6957 15.6956 28.6957H35.1738C36.2783 28.6957 37.1738 27.8002 37.1738 26.6957V19.0818C37.1738 17.3135 39.3001 16.4151 40.5679 17.6478L59.177 35.74Z' fill='white'/>
				</svg>
			</button>
		</div>;
	}
}
 
export default Practice;
